"use client";

import { useState, useEffect } from "react";
import { ChevronLeft, Clock, Lock, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QUIZ_CONFIG, WHATSAPP_CONFIG, VSL_CONFIG, QUIZ_VIDEOS, QUIZ_TIMERS } from "@/config/constants";
import { VturbPlayer } from "@/components/VturbPlayer";

type QuizVersion = "v1" | "v2";

export default function Quiz() {
  const [version, setVersion] = useState<QuizVersion>("v1");
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showCamillaFlow, setShowCamillaFlow] = useState(false);
  const [showVideoButton, setShowVideoButton] = useState(false);
  const [showCamillaButtons, setShowCamillaButtons] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    // Verificar query parameter para versão
    const searchParams = new URLSearchParams(window.location.search);
    const vParam = searchParams.get("v");
    if (vParam === "v2") {
      setVersion("v2");
    } else {
      setVersion("v1");
    }
    setIsLoading(false);
  }, []);

  // Efeito para mostrar botão do vídeo após tempo configurado
  useEffect(() => {
    const quizData = QUIZ_CONFIG[version];
    const currentQuestion = currentStep === 0 ? null : quizData.questions[currentStep - 1];
    
    if (currentQuestion?.type === "video") {
      setShowVideoButton(false);
      const timer = setTimeout(() => {
        setShowVideoButton(true);
      }, QUIZ_TIMERS.marlon_video); // Usa o tempo do config
      return () => clearTimeout(timer);
    } else {
      setShowVideoButton(false);
    }
  }, [currentStep, version]);

  // Efeito para mostrar botões do vídeo da Camilla após tempo configurado
  useEffect(() => {
    if (showCamillaFlow) {
      setShowCamillaButtons(false);
      const timer = setTimeout(() => {
        setShowCamillaButtons(true);
      }, QUIZ_TIMERS.camilla_video); // Usa o tempo do config
      return () => clearTimeout(timer);
    } else {
      setShowCamillaButtons(false);
    }
  }, [showCamillaFlow]);

  if (isLoading) return null;

  const quizData = QUIZ_CONFIG[version];
  const totalSteps = quizData.questions.length + 1; // +1 para abertura
  const currentQuestion = currentStep === 0 ? null : quizData.questions[currentStep - 1];

  const handleAnswer = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentStep]: value }));

    // Se for pergunta de qualificação, lidar com flows especiais
    if (currentQuestion?.type === "qualification") {
      const selectedOption = currentQuestion.options.find((opt) => opt.value === value) as any;
      if (selectedOption?.flow === "video_camilla") {
        // Mostrar vídeo da Camilla
        setShowCamillaFlow(true);
        return;
      } else if (selectedOption?.flow === "community" || selectedOption?.flow === "whatsapp") {
        // Ir direto para o link
        const link = selectedOption.flow === "community" ? WHATSAPP_CONFIG.groupLink : WHATSAPP_CONFIG.quizLink;
        window.location.href = link;
      }
      return;
    }

    // Próximo passo
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const handleVideoComplete = () => {
    // Quando clica em "QUERO ENTRAR NO GRUPO GRATUITO", mostra o vídeo da Camilla
    setShowCamillaFlow(true);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Renderizar tela de abertura
  if (currentStep === 0) {
    return (
      <main className="relative min-h-screen w-full overflow-hidden flex items-center justify-center px-4">
        {/* Background animado */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-primary">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary/30 rounded-full blur-[100px] motion-safe:animate-glow-pulse" aria-hidden />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-[100px] motion-safe:animate-glow-pulse" style={{ animationDelay: "2s" }} aria-hidden />
        </div>

        {/* Grid background */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.03] [mask-image:radial-gradient(70%_70%_at_50%_50%,black,transparent)]"
          style={{
            backgroundImage:
              "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Conteúdo */}
        <div className="relative z-10 w-full max-w-2xl">
          <div className="text-center space-y-8 motion-safe:animate-fade-in">
            {/* Título Principal */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black leading-tight">
                A melhor comunidade para quem quer sair do ciclo das
                <span className="bg-gradient-accent bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(100,192,242,0.6)]">
                  {" "}apostas.
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-lg mx-auto">
                Mas antes, precisamos confirmar se você realmente está disposto a recuperar sua vida.
                <br/>
                Responda com honestidade às perguntas abaixo para liberar seu acesso ao grupo.
              </p>
            </div>

            {/* Badges informativos */}
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <span>Rápido: 2 minutos</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-primary" />
                <span>Totalmente anônimo</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-primary" />
                <span>Sem julgamento</span>
              </div>
            </div>

            {/* CTA */}
            <Button
              variant="hero"
              size="xl"
              onClick={() => setCurrentStep(1)}
              className="mt-8"
            >
              COMEÇAR AGORA
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen w-full overflow-hidden px-4 py-8">
      {/* Background animado */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-primary">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/30 rounded-full blur-[100px] motion-safe:animate-glow-pulse" aria-hidden />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-[100px] motion-safe:animate-glow-pulse" style={{ animationDelay: "2s" }} aria-hidden />
      </div>

      {/* Grid background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.03] [mask-image:radial-gradient(70%_70%_at_50%_50%,black,transparent)]"
        style={{
          backgroundImage:
            "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Conteúdo */}
      <div className="relative z-10 w-full max-w-2xl mx-auto">
        {/* Header com botão voltar */}
        <div className="flex items-center justify-end mb-8">
          <div className="text-sm text-muted-foreground">
            Pergunta {currentStep} de {totalSteps - 1}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mb-12">
          <div
            className="h-full bg-gradient-to-r from-cyan-600 to-blue-600 transition-all duration-300"
            style={{ width: `${(currentQuestion?.progress || 0)}%` }}
          />
        </div>

        {/* Fluxo Camilla - Vídeo de confirmação mentoria */}
        {showCamillaFlow && (
          <div className="space-y-8 motion-safe:animate-fade-in">
            {/* Título */}
            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl font-black leading-tight whitespace-pre-line">
                Só uma verificação rápida da Camilla:
              </h2>
            </div>

            {/* Vídeo da Camilla */}
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/20">
              <VturbPlayer
                videoId={QUIZ_VIDEOS.camilla.id}
                scriptUrl={QUIZ_VIDEOS.camilla.url}
                className="w-full rounded-2xl"
              />
            </div>

            {!showCamillaButtons && (
              <div className="text-center text-muted-foreground">
                Assista o vídeo até o final...
              </div>
            )}

            {/* Opções após vídeo Camilla */}
            {showCamillaButtons && (
              <div className="space-y-3">
                <button
                  onClick={() => window.location.href = WHATSAPP_CONFIG.mentoriaLink}
                  className="w-full px-6 py-4 text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-accent/50 rounded-xl transition-all duration-300 font-semibold text-lg group"
                >
                  <span className="group-hover:text-accent transition-colors">
                    ✅ Eu consigo R$200,00 para me ajudar
                  </span>
                </button>
                <button
                  onClick={() => window.location.href = WHATSAPP_CONFIG.groupLink}
                  className="w-full px-6 py-4 text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-accent/50 rounded-xl transition-all duration-300 font-semibold text-lg group"
                >
                  <span className="group-hover:text-accent transition-colors">
                    ❌ Eu não consigo, quero só o grupo
                  </span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pergunta */}
        {currentQuestion && !showCamillaFlow && (
          <div className="space-y-8 motion-safe:animate-fade-in">
            {/* Título */}
            <div className="space-y-4">
              {currentQuestion.type === "value" ? (
                <div className="space-y-6">
                  <h2 className="text-2xl sm:text-3xl font-bold leading-relaxed">
                    <span className="text-muted-foreground">Você está prestes a entrar no grupo que pode mudar completamente sua relação com as apostas.</span>
                  </h2>
                  <h3 className="text-2xl sm:text-4xl font-black leading-tight">
                    Mas antes, precisamos confirmar uma coisa:{" "}
                    <span className="bg-gradient-accent bg-clip-text text-transparent">
                      Você está realmente disposto a seguir o processo?
                    </span>
                  </h3>
                </div>
              ) : (
                <h2 className="text-3xl sm:text-4xl font-black leading-tight whitespace-pre-line">
                  {currentQuestion.title}
                </h2>
              )}
            </div>

            {/* Vídeo - Marlon explicando */}
            {currentQuestion.type === "video" && (
              <div className="space-y-6">
                <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/20">
                  <VturbPlayer
                    videoId={QUIZ_VIDEOS.marlon.id}
                    scriptUrl={QUIZ_VIDEOS.marlon.url}
                    className="w-full rounded-2xl"
                  />
                </div>
                
                {!showVideoButton && (
                  <div className="text-center text-muted-foreground">
                    Assista o vídeo até o final...
                  </div>
                )}
                
                {showVideoButton && (
                  <div className="space-y-3">
                    <Button
                      variant="hero"
                      size="xl"
                      onClick={() => window.location.href = WHATSAPP_CONFIG.mentoriaLink}
                      className="w-full"
                    >
                      💎 Tenho interesse na mentoria individual
                    </Button>
                    <Button
                      variant="outline"
                      size="xl"
                      onClick={handleVideoComplete}
                      className="w-full"
                    >
                      QUERO ENTRAR NO GRUPO GRATUITO
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Tela de Valor - Benefícios */}
            {currentQuestion.type === "value" && (
              <div className="space-y-6">
                {/* Lista de benefícios */}
                <div className="space-y-4 bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-2xl p-6 md:p-8">
                  {(currentQuestion as any).benefits?.map((benefit: string, idx: number) => (
                    <div
                      key={idx}
                      className="flex items-start gap-4 pb-4 border-b border-white/5 last:border-0 last:pb-0"
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-accent" />
                      </div>
                      <p className="text-lg text-foreground leading-relaxed font-medium">
                        {benefit}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Botão continuar */}
                <Button
                  variant="hero"
                  size="xl"
                  onClick={() => handleAnswer("confirmed")}
                  className="w-full mt-8"
                >
                  {(currentQuestion as any).buttonText}
                </Button>
              </div>
            )}

            {/* Opções - Perguntas regulares e qualificação */}
            {currentQuestion.type !== "value" && currentQuestion.type !== "video" && currentQuestion.options && (
              <div className="space-y-3">
                {currentQuestion.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(option.value)}
                    className="w-full px-6 py-4 text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-accent/50 rounded-xl transition-all duration-300 font-semibold text-lg group"
                  >
                    <span className="group-hover:text-accent transition-colors">
                      {option.text}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
